/**
 * Appointment Management list: closing & check-in column display + text colors.
 *
 * Palette (darker, heavier text — strong contrast on white backgrounds):
 * - Booking: forest green
 * - Not Interested: burgundy red
 * - Revisit: burnt orange
 * - Follow-up: navy blue
 * - Visited: deep teal
 * - Upcoming Revisit / Revisit Scheduled: deep violet
 * - Empty → "-" via formatAppointmentCellValue (charcoal)
 */

/** @type {Record<string, string>} */
export const APPOINTMENT_STATUS_COLORS = {
  /** Booked / Booking / Ready to book */
  booking: "#008000",
  notInterested: "#ff0000",
  /** Closing disposition "Revisit" (not "Upcoming Revisit" / not scheduled) */
  revisit: "#9A3B00",
  followup: "#000099",
  /** Check-in column "Visited" */
  visited: "#00cc00",
  upcomingRevisit: "#ff0000",
  /** "-" and unknown / generic pending */
  neutral: "#3D4044",
  upcoming: "#2A3036",
  default: "#0D0F12",
};

/**
 * Show "-" when there is no meaningful text (after trim).
 * @param {unknown} value
 * @returns {string}
 */
export function formatAppointmentCellValue(value) {
  const t = String(value ?? "").trim();
  return t === "" ? "-" : t;
}

/**
 * Text color for Closing status cell from the label shown (raw, before formatAppointmentCellValue).
 * @param {unknown} label
 * @returns {string} CSS color
 */
export function getClosingStatusTextColor(label) {
  const raw = String(label ?? "").trim();
  if (!raw) return APPOINTMENT_STATUS_COLORS.neutral;

  const s = raw.toLowerCase().replace(/\s+/g, " ");

  if (
    s.includes("not interested") ||
    s.includes("not fit for sale") ||
    s === "not fit" ||
    s.includes("visit cancelled")
  ) {
    return APPOINTMENT_STATUS_COLORS.notInterested;
  }

  if (
    /\b(booked|booking)\b/i.test(raw) ||
    /ready\s+to\s+book/i.test(raw) ||
    /book\s+now/i.test(raw)
  ) {
    return APPOINTMENT_STATUS_COLORS.booking;
  }

  if (s.includes("upcoming revisit") || s.includes("revisit scheduled")) {
    return APPOINTMENT_STATUS_COLORS.upcomingRevisit;
  }

  if (
    /\bfollow\b/.test(s) ||
    /\bcall\b/.test(s) ||
    /\bwhatsapp\b/.test(s) ||
    s.includes("next day call") ||
    s.includes("call/whatsapp") ||
    s.includes("call / whatsapp")
  ) {
    return APPOINTMENT_STATUS_COLORS.followup;
  }

  if (s.includes("revisit")) {
    return APPOINTMENT_STATUS_COLORS.revisit;
  }

  if (s === "not visited" || s === "notvisited") {
    return APPOINTMENT_STATUS_COLORS.upcoming;
  }

  if (
    /\bvisited\b/.test(s) ||
    s.includes("site visit done") ||
    s === "complete"
  ) {
    return APPOINTMENT_STATUS_COLORS.visited;
  }

  if (s === "not updated") {
    return APPOINTMENT_STATUS_COLORS.neutral;
  }

  if (s === "upcoming" || s.includes("reschedule")) {
    return APPOINTMENT_STATUS_COLORS.upcoming;
  }

  return APPOINTMENT_STATUS_COLORS.default;
}

/**
 * Check-in column: Visited = teal; pending / "-" = neutral.
 * @param {boolean} showAsVisited
 * @returns {string} CSS color
 */
export function getCheckInStatusTextColor(showAsVisited) {
  return showAsVisited
    ? APPOINTMENT_STATUS_COLORS.visited
    : APPOINTMENT_STATUS_COLORS.neutral;
}
